import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Cache de id → etiqueta legible, compartida por toda la app. */
const cache = new Map<string, string>();

export function isPaymentMethodId(value?: string | null): boolean {
  return !!value && UUID_RE.test(value);
}

/**
 * Etiqueta legible para `reservations.balance_payment_method`.
 * El campo guarda un UUID de `payment_methods` (o el literal "pagos_divididos"),
 * nunca debe mostrarse crudo en pantalla.
 */
export function usePaymentMethodLabel(value?: string | null): string {
  const [label, setLabel] = useState<string>(() => {
    if (!value) return "";
    if (value === "pagos_divididos") return "Pagos divididos";
    if (!isPaymentMethodId(value)) return value;
    return cache.get(value) ?? "Método de pago";
  });

  useEffect(() => {
    if (!value) {
      setLabel("");
      return;
    }
    if (value === "pagos_divididos") {
      setLabel("Pagos divididos");
      return;
    }
    if (!isPaymentMethodId(value)) {
      setLabel(value);
      return;
    }
    const cached = cache.get(value);
    if (cached) {
      setLabel(cached);
      return;
    }
    let active = true;
    (async () => {
      const { data } = await supabase
        .from("payment_methods")
        .select("name, owner_name")
        .eq("id", value)
        .maybeSingle();
      const resolved = data
        ? data.owner_name
          ? `${data.name} (${data.owner_name})`
          : data.name
        : "Método de pago";
      cache.set(value, resolved);
      if (active) setLabel(resolved);
    })();
    return () => {
      active = false;
    };
  }, [value]);

  return label;
}
